import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import type { Quote } from '../api/types'
import { QuoteMutationClient, quoteLineApi, type QuoteEdit, type QuoteMutationFailure } from './edits'

/**
 * The demo's `QuoteEditorView` state: the visible quote (server copy plus edits in flight), per-edit
 * failures with retry, the stale-revision conflict and its reload. `quote` is the shell's copy; a
 * newer revision from anywhere else (assistant, import, another tab) replaces the server copy.
 */
export function useQuoteEditor(quote: Quote, onQuoteChange: (quote: Quote) => void) {
  const api = useQuoteBuilderApi()
  const lineApi = useMemo(() => quoteLineApi(api), [api])
  const queryClient = useQueryClient()
  const [visible, setVisible] = useState(quote)
  const [failures, setFailures] = useState<Record<string, QuoteMutationFailure>>({})
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [alternativesSaving, setAlternativesSaving] = useState(false)
  const client = useRef<QuoteMutationClient | null>(null)
  const onChange = useRef(onQuoteChange)
  useEffect(() => {
    onChange.current = onQuoteChange
  })

  const persisted = (next: Quote) => {
    setConflict(false)
    setError('')
    onChange.current(next)
    void queryClient.invalidateQueries({
      queryKey: ['quote-versions', next.id],
    })
    void queryClient.invalidateQueries({
      queryKey: ['quote-activity', next.id],
    })
  }

  const makeClient = (base: Quote) =>
    new QuoteMutationClient(
      base,
      () => lineApi.read(base.id),
      (next, pendingCount) => {
        setVisible(next)
        setSaving(pendingCount > 0)
      },
      (failure) => {
        setFailures((current) => ({ ...current, [failure.edit.key]: failure }))
        if (failure.edit.kind === 'alternatives') setAlternativesSaving(false)
        if (failure.error instanceof QuoteBuilderApiError && failure.error.status === 412) {
          setConflict(true)
          setError('The quote changed again while your edit was saving. Reload the latest quote, then retry.')
        } else setError('One edit was not saved. Its prior value is back; retry beside that field.')
      },
      persisted,
    )

  useEffect(() => {
    const created = makeClient(quote)
    client.current = created
    setVisible(quote)
    return () => {
      created.dispose()
      if (client.current === created) client.current = null
    }
    // One edit queue per quote; later copies arrive through replaceServerQuote.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.id, lineApi])

  useEffect(() => {
    client.current?.replaceServerQuote(quote)
  }, [quote])

  const run = (edit: QuoteEdit) => {
    setFailures((current) => {
      const next = { ...current }
      delete next[edit.key]
      return next
    })
    if (!client.current || client.current.isDisposed) client.current = makeClient(visible)
    client.current.enqueue(edit)
  }

  const retry = (key: string) => {
    const failure = failures[key]
    if (failure) run(failure.edit)
  }

  const reload = async () => {
    setReloading(true)
    try {
      const latest = await lineApi.read(quote.id)
      client.current?.replaceServerQuote(latest)
      onChange.current(latest)
      setConflict(false)
      setError('')
    } catch {
      setError('The latest quote could not be loaded. Check the connection and try again.')
    } finally {
      setReloading(false)
    }
  }

  /** Adopt a quote saved outside the queue (the import workspace). */
  const adopt = (next: Quote) => {
    client.current?.replaceServerQuote(next)
    persisted(next)
  }

  return {
    api,
    lineApi,
    quote: visible,
    failures,
    error,
    conflict,
    reloading,
    saving,
    alternativesSaving,
    setAlternativesSaving,
    dismissError: () => setError(''),
    run,
    retry,
    reload,
    adopt,
  }
}

export type QuoteEditor = ReturnType<typeof useQuoteEditor>
